import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { loadGeneratedCodeList, loadLatestCodeGeneration } from "~/services/chat.services";
import { createSelectors } from "./createSelectorsFunction";
import useChatMessagesStore from "./chatMessagesStore";

type CodeGeneratorState = {
    latestCode: string;
    generatedCodeList: string[];
    currentPage: number;
    hasMore: boolean;
    isLoadingHistory: boolean;
    loadingProgress: number;
    loadingMessage: string;
    actions: {
        loadGeneratedCodeList: (historyId: number, from: string, pageToLoad?: number) => Promise<void>;
        addCodeGenerated: (code: string, from: string) => void;
        loadLatestCodeGenerated: (historyId: number, from: string) => Promise<string>;
        clearGenerations: () => void;
        setLoadingProgress: (progress: number) => void;
        setLoadingMessage: (message: string) => void;
        setProgress: (progress: number, message: string) => void;
        clearLoadingState: () => void;
    };
}

const initialState: Omit<CodeGeneratorState, 'actions'> = {
    latestCode: '',
    generatedCodeList: [],
    currentPage: 0,
    hasMore: false,
    isLoadingHistory: false,
    loadingProgress: 0,
    loadingMessage: 'Starting code generation'
}

const codeGeneratorStore = immer<CodeGeneratorState>((set: any, get: any) => ({
    ...initialState,
    actions: {
        loadGeneratedCodeList: async (historyId: number, from: string, pageToLoad?: number) => {
            const page = pageToLoad ?? get().currentPage + 1;

            if (get().isLoadingHistory) {
                console.log("Code Generator History loading skipped:", { isLoading: get().isLoadingHistory, hasMore: get().hasMore });
                return;
            }

            set({ isLoadingHistory: true }, false, 'Code Generator History Loading');
            try {
                const data = await loadGeneratedCodeList(historyId, page);
                const generatedCodeList = data.generatedCodeList || [];
                const receivedHasMore = data.hasMore ?? false;

                set((state: CodeGeneratorState) => ({
                    generatedCodeList: generatedCodeList,
                    currentPage: page,
                    hasMore: receivedHasMore,
                    isLoadingHistory: false
                }), false, 'Code Generator History Loading: ' + from);
            } catch (error) {
                console.error("Error loading code generator history:", error);
                set({ isLoadingHistory: false }, false, 'Code Generator History Loading Error');
                throw error;
            }
        },
        addCodeGenerated: (code: string, from: string) => {
            set({ latestCode: code }, false, 'Add Code Generated: ' + from);
            set({ generatedCodeList: [...get().generatedCodeList, code] }, false, 'Add Code Generated: ' + from);
        },
        loadLatestCodeGenerated: async (historyId: number, from: string) => {
            console.log('Loading latest code for projectId:', historyId);
            const data = await loadLatestCodeGeneration(historyId);

            // Extract code from response (returnedObject already extracted by interceptor)
            const latestCode = data?.code || '';

            console.log('Code API response data:', data);
            console.log('Loaded code length:', latestCode.length);
            console.log('Loaded code preview:', latestCode.substring(0, 200));

            // Check for error codes in the code itself
            const isErrorCode = latestCode === 'GENERIC_ERROR' ||
                latestCode.startsWith('HTTP_ERROR') ||
                (latestCode.startsWith('ERROR') && latestCode.length > 5);

            if (isErrorCode) {
                console.warn('Code API returned an error code:', latestCode);
                // Don't set code if it's an error
                set({ latestCode: '' }, false, 'Load Latest Code Generated: ' + from + ' (Error)');

                // Don't show editor for error codes
                const chatMessagesStoreState = useChatMessagesStore.getState();
                chatMessagesStoreState.actions.setHasGeneratedCode(false);
                chatMessagesStoreState.actions.setHasCode(false);
                chatMessagesStoreState.actions.setIsEditorVisible(false);
                console.log('Error code detected, editor hidden');
                return '';
            }

            set({ latestCode: latestCode }, false, 'Load Latest Code Generated: ' + from);

            // If code was loaded successfully, set flags in chatMessagesStore
            if (latestCode && latestCode.trim() !== '') {
                const chatMessagesStoreState = useChatMessagesStore.getState();
                chatMessagesStoreState.actions.setHasGeneratedCode(true);
                chatMessagesStoreState.actions.setHasCode(true);
                chatMessagesStoreState.actions.setIsEditorVisible(true);
                console.log('Code loaded successfully, flags set. Editor should be visible:', chatMessagesStoreState.isEditorVisible);

                // Double-check editor visibility is set (in case subscription didn't fire)
                setTimeout(() => {
                    const currentState = useChatMessagesStore.getState();
                    if (!currentState.isEditorVisible && latestCode && latestCode.trim() !== '') {
                        console.warn('Editor visibility not set after code load, forcing it now');
                        currentState.actions.setIsEditorVisible(true);
                        currentState.actions.setHasCode(true);
                        currentState.actions.setHasGeneratedCode(true);
                    }
                }, 100);
            } else {
                console.log('No code found in response');
                // Don't hide editor if code is empty - it might be loading or not generated yet
                // Only hide if we explicitly detected an error above
            }

            return latestCode;
        },
        clearGenerations: () => {
            set((state: CodeGeneratorState) => ({
                ...state,
                latestCode: '',
                generatedCodeList: [],
                currentPage: 0,
                hasMore: false,
                isLoadingHistory: false,
                loadingProgress: 0,
                loadingMessage: ''
            }), false, 'Clear All Generations');
        },
        setLoadingProgress: (progress: number) => {
            set({ loadingProgress: progress }, false, 'Set Loading Progress');
        },
        setLoadingMessage: (message: string) => {
            set({ loadingMessage: message }, false, 'Set Loading Message');
        },
        setProgress: (progress: number, message: string) => {
            set({ loadingProgress: progress, loadingMessage: message }, false, 'Set Progress');
        },
        clearLoadingState: () => {
            set({ loadingProgress: 0, loadingMessage: '' }, false, 'Clear Loading State');
        }
    }
}));

const useCodeGeneratorStoreBase = create(
    subscribeWithSelector(devtools(codeGeneratorStore, { name: 'CodeGeneratorStore' }))
);
const useCodeGeneratorStore = createSelectors(useCodeGeneratorStoreBase)

export default useCodeGeneratorStore;
export const useCodeGeneratorStoreActions = () => useCodeGeneratorStore((state: any) => state.actions);

